/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  EventActionAttempt,
  ExecutionStatus,
  BatchActionFragment,
  EventActionFragment,
  EventAction,
  IncomingEventsListBatchActionFragment,
  IncomingEventsListPlanActionFragment,
  IncomingEventsListPlanActionBatchFragment,
  IncomingEventDetailPlanBatchActionFragment,
  ExecutionPolicy,
  SkipWindowType,
  EventDetailResourceGroupFragment,
  Maybe,
  EventActionBatchFragment,
  BatchAction,
  EventDetailResourceFragment,
  BatchedEventAction,
  EventViewAction,
  EventViewActionBatch,
} from "~/generated/graphql";
import { ToggleItem } from "~/components/ToggleItemBox";

export enum Period {
  week = "WEEK",
  month = "MONTH",
  year = "YEAR",
  range = "RANGE",
}
export enum EventType {
  pastCurrent = "PAST_CURRENT",
  future = "FUTURE",
}

export enum EventsFilterFields {
  startDate = "startDate",
  endDate = "endDate",
}

export type EventsFiltersType = {
  startDate?: string;
  endDate?: string;
};

export type Item = ToggleItem & {
  onSelect?: (value: EventActionAttempt) => void;
};

export enum EntityType {
  EVENT = "Event",
  FUTURE_EVENT = "FutureEvent",
  FUTURE_ACTION_BATCH = "FutureActionBatch",
  ACTION = "Action",
  ACTION_BATCH = "ActionBatch",
}

export type EventEntity = {
  __typename?:
    | EventActionFragment["__typename"]
    | EventActionBatchFragment["__typename"]
    | BatchAction["__typename"]
    | BatchActionFragment["__typename"]
    | EventViewAction["__typename"]
    | EventViewActionBatch["__typename"];
  id: string;
  name: string;
  action?: EventActionFragment["action"];
  actionName?: EventAction["actionName"];
  resourcesSucceeded?: number;
  resourcesFailed?: number;
  resourcesNotCompleted?: number;
  errorCodeCounts?: EventViewAction["errorCodeCounts"];
  inputParameters?: EventAction["inputParameters"];
  scope?: EventActionFragment["scope"];
  order?: EventActionFragment["order"];
  attempts?: EventActionAttempt[];
  executionPolicy?: EventActionFragment["executionPolicy"];
  windowDuration?: number | null;
  skipWindow?: EventActionFragment["skipWindow"];
  status?: ExecutionStatus;
  runInSequence?: EventAction["runInSequence"];
  globalState?: {
    status: ExecutionStatus;
    estimatedStartTime?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    outputUri?: string | null;
  } | null;
  actions?: EventEntity[];
  subRows?: EventEntity[];
  resourceStates?:
    | BatchActionFragment["resourceStates"]
    | EventActionFragment["resourceStates"];
  resourceGroups?:
    | BatchActionFragment["resourceGroups"]
    | EventActionFragment["resourceGroups"];
  resources?: BatchActionFragment["resources"] | EventAction["resources"];
  startTime?: EventActionBatchFragment["startTime"];
};

export type PlanEntityIncomingEventDetails = {
  id: string;
  name: string;
  order?: number;
  action?: IncomingEventDetailPlanBatchActionFragment["action"];
  actionName?: EventAction["actionName"];
  batchActions?: (IncomingEventDetailPlanBatchActionFragment & {
    entityType: string;
  })[];
  resourceGroups?: Maybe<EventDetailResourceGroupFragment[]>;
  resources?: Maybe<EventDetailResourceFragment[]>;
  entityType: string;
  runInSequence?: EventAction["runInSequence"];
  executionPolicy?: ExecutionPolicy;
  skipWindow?: SkipWindowType;
  windowDuration?: number;
  inputParameters?: IncomingEventDetailPlanBatchActionFragment["inputParameters"];
};

export type EventActionItem = ToggleItem & {
  onSelect?: (value: EventActionAttempt | undefined) => void;
};

export type PlanEntityIncomingEventList = {
  id: string;
  name: string;
  order?: number;
  action?: IncomingEventsListPlanActionFragment["action"];
  batchActions?: IncomingEventsListPlanActionBatchFragment["batchActions"];
  subRows?: IncomingEventsListBatchActionFragment[];
  resourceGroups?:
    | IncomingEventsListPlanActionFragment["resourceGroups"]
    | IncomingEventsListBatchActionFragment["resourceGroups"];
};

export type FlatEventEntity =
  | EventAction
  | BatchedEventAction
  | PlanEntityIncomingEventDetails;
